import { CellClassParams, ColDef } from "ag-grid-community";
import { aggridHeaderClass, commonBorderStyle, commonNumberCellStyle, commonTextCellStyle } from "src/components/common/common";

// 피벗 그리드의 "구분" 행 1개를 정의하는 설정
export interface PivotRowConfig<T> {
	field: keyof T;      // 원본 DTO에서 기간별 값을 추출할 필드명
	label: string;       // 구분 컬럼에 표시할 라벨
}

// 원본 데이터 리스트에서 특정 필드의 값을 기간(월/주/분기 등) 순서에 맞춰 숫자 배열로 추출하는 함수 타입.
// 예: chartUtils의 buildMonthlySeries (mm 1~12 기준 월별 추출)
export type PivotSeriesBuilder<T> = (data: T[], field: keyof T) => number[];

// 기간별 값 배열로부터 "평균" 값을 계산하는 함수 타입 (기본은 산술 평균)
export type PivotAverageBuilder = (values: number[]) => number;

// Grid_Chart에 넣을 flat 행 데이터 (구분/기간별 값). 평균은 저장하지 않고 averageColumn()이 values로부터 직접 계산한다.
export interface PivotRowData {
	division: string;
	values: number[]; // 기간별 값 (월별이면 12개, 다른 기간 단위면 다른 개수)
	inserted?: boolean; // GridContextWrapper 제네릭 제약 충족용 (읽기 전용 그리드이므로 실제 사용 안 함)
}

// 기본 평균 계산 함수: 숫자 배열의 산술 평균 (빈 배열이면 0 반환)
export const defaultAverageBuilder: PivotAverageBuilder = (arr) =>
	arr.length === 0 ? 0 : arr.reduce((s, v) => s + v, 0) / arr.length;

// 숫자를 소수 2자리 + 천단위 콤마 형식 문자열로 변환 (기존 Table_PRD*와 동일한 포맷)
const fmt = (n: number) =>
	Number(n ?? 0).toLocaleString("ko-KR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * 결과 리스트를 Grid_Chart용 flat 피벗 행 데이터로 변환한다.
 * rows에 정의된 순서대로 구분별 1개 행씩 생성한다.
 * @param data 원본 데이터 리스트
 * @param rows 구분별 행 설정 목록
 * @param seriesBuilder 필드값을 기간 순서에 맞춰 숫자 배열로 추출하는 함수 (예: chartUtils의 buildMonthlySeries)
 */
export const buildPivotRows = <T>(
	data: T[],
	rows: PivotRowConfig<T>[],
	seriesBuilder: PivotSeriesBuilder<T>,
): PivotRowData[] => {
	return rows.map((row) => ({
		division: row.label,
		values: seriesBuilder(data, row.field),
	}));
};

/**
 * "구분" 리딩 컬럼 1개를 생성한다. Grid_Chart의 leadingColumnDefs 배열에 직접 넣어 사용할 수 있다.
 */
export const divisionColumn = (): ColDef<PivotRowData> => ({
	headerName: "구분", field: "division", colId: "division",
	width: 110, pinned: "left",
	cellStyle: (params) => ({
		...commonBorderStyle,
		...commonTextCellStyle,
		textAlign: "center",
		backgroundColor: "#F9FAFB",
	}),
	sortable: false, resizable: true, suppressMovable: true, headerClass: aggridHeaderClass,
});

/**
 * "평균" 리딩 컬럼 1개를 생성한다. Grid_Chart의 leadingColumnDefs 배열에 직접 넣어 사용할 수 있다.
 * row.values로부터 직접 평균을 계산해 표시한다 (row 데이터에는 평균값을 별도로 저장하지 않음).
 * @param averageBuilder 평균값 계산 함수. 생략하면 기본 산술 평균을 사용한다.
 */
export const averageColumn = (averageBuilder: PivotAverageBuilder = defaultAverageBuilder): ColDef<PivotRowData> => ({
	headerName: "평균", colId: "average",
	width: 90,
	valueGetter: (params) => averageBuilder(params.data?.values ?? []),
	valueFormatter: (params) => fmt(params.value),
	cellStyle: () => ({
		...commonBorderStyle,
		...commonNumberCellStyle,
	}),
	sortable: false, resizable: true, suppressMovable: true, headerClass: aggridHeaderClass,
});

// 대부분의 화면에서 공통으로 쓰는 기본 리딩 컬럼 조합 (구분 + 평균)
export const DEFAULT_LEADING_COLUMNS: ColDef<PivotRowData>[] = [divisionColumn(), averageColumn()];

/**
 * periodLabels 기준으로 Grid_Chart의 AG Grid 컬럼 정의(리딩 컬럼 + 기간별 값)를 생성한다.
 * @param periodLabels 기간별 컬럼 헤더 라벨 목록 (예: chartUtils의 MONTH_LABELS)
 * @param leadingColumnDefs 기간별 값 컬럼 앞에 그대로 붙일 컬럼 목록. 구분/평균 컬럼이 필요하면 divisionColumn()/averageColumn()을 넣고,
 * 필요 없으면 빼거나 계산식 등 커스텀 ColDef를 넣으면 된다 (화면마다 필요한 조합이 다를 수 있어 호출부에서 명시적으로 구성한다).
 */
export const pivotColumnDefs = <T>(
	periodLabels: string[],
	leadingColumnDefs: ColDef<PivotRowData>[],
): ColDef<PivotRowData>[] => {

	const numberCellStyle = (params: CellClassParams<PivotRowData>) => ({
		...commonBorderStyle,
		...commonNumberCellStyle,
		fontWeight: 400,
	});

	const columnDefs: ColDef<PivotRowData>[] = [...leadingColumnDefs];

	periodLabels.forEach((label, index) => {
		columnDefs.push({
			headerName: label, colId: `v${index}`,
			valueGetter: (params) => params.data?.values?.[index],
			valueFormatter: (params) => fmt(params.value),
			cellStyle: numberCellStyle,
			width: 120,
			sortable: false, resizable: true, suppressMovable: true, headerClass: aggridHeaderClass,
		});
	});

	return columnDefs;
};
